/** Icon bank shared by company values and strategic pillars — metadata only (no SVG rendering), 206 entries. */

export interface ValueIconMeta {
    id: string;
    name: string;
    keywords: string[];
    bg: string;
}

// Icons representing meaningful company values and strategic pillars
export const iconBank: ValueIconMeta[] = [
    // Core Values & Ethics
    {
        id: 'shield',
        name: 'Integrity',
        keywords: ['protection', 'security', 'safety', 'integrity', 'trust'],
        bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
        id: 'heart',
        name: 'Passion',
        keywords: ['love', 'passion', 'care', 'emotion', 'dedication'],
        bg: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)',
    },
    {
        id: 'hand-heart',
        name: 'Empathy',
        keywords: ['empathy', 'support', 'help', 'kindness', 'care'],
        bg: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
    },
    {
        id: 'balance',
        name: 'Fairness',
        keywords: ['justice', 'fairness', 'equality', 'harmony', 'ethics'],
        bg: 'linear-gradient(135deg, #b79891 0%, #94716b 100%)',
    },
    {
        id: 'handshake',
        name: 'Trust',
        keywords: ['partnership', 'deal', 'agreement', 'collaboration', 'trust'],
        bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
        id: 'anchor',
        name: 'Reliability',
        keywords: ['stability', 'foundation', 'secure', 'grounded', 'reliable'],
        bg: 'linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)',
    },
    {
        id: 'compass',
        name: 'Guidance',
        keywords: ['direction', 'guidance', 'navigation', 'explore', 'purpose'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'lighthouse',
        name: 'Leadership',
        keywords: ['guide', 'beacon', 'direction', 'leader', 'vision'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    // Excellence & Achievement
    {
        id: 'star',
        name: 'Excellence',
        keywords: ['excellence', 'quality', 'premium', 'best', 'star'],
        bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
        id: 'diamond',
        name: 'Quality',
        keywords: ['value', 'precious', 'quality', 'luxury', 'excellence'],
        bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    {
        id: 'crown',
        name: 'Leadership',
        keywords: ['leadership', 'king', 'authority', 'excellence', 'prestige'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'trophy',
        name: 'Achievement',
        keywords: ['winner', 'achievement', 'success', 'award', 'excellence'],
        bg: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    },
    {
        id: 'medal',
        name: 'Recognition',
        keywords: ['honor', 'recognition', 'achievement', 'award', 'merit'],
        bg: 'linear-gradient(135deg, #c79081 0%, #dfa579 100%)',
    },
    {
        id: 'ribbon',
        name: 'Distinction',
        keywords: ['award', 'honor', 'distinction', 'achievement', 'excellence'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'laurel',
        name: 'Honor',
        keywords: ['victory', 'honor', 'achievement', 'glory', 'success'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        id: 'badge',
        name: 'Pride',
        keywords: ['badge', 'honor', 'pride', 'recognition', 'identity'],
        bg: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)',
    },
    // Innovation & Growth
    {
        id: 'lightbulb',
        name: 'Innovation',
        keywords: ['idea', 'innovation', 'creative', 'thinking', 'insight'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'rocket',
        name: 'Ambition',
        keywords: ['launch', 'growth', 'startup', 'innovation', 'ambition'],
        bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
        id: 'lightning',
        name: 'Energy',
        keywords: ['energy', 'power', 'speed', 'fast', 'dynamic'],
        bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
        id: 'growth',
        name: 'Progress',
        keywords: ['progress', 'chart', 'increase', 'success', 'growth'],
        bg: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    },
    {
        id: 'arrow-up',
        name: 'Improvement',
        keywords: ['progress', 'improve', 'rise', 'advance', 'growth'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        id: 'mountain',
        name: 'Aspiration',
        keywords: ['challenge', 'achievement', 'peak', 'climb', 'ambition'],
        bg: 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)',
    },
    {
        id: 'sunrise',
        name: 'Optimism',
        keywords: ['hope', 'new', 'beginning', 'optimism', 'future'],
        bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    },
    {
        id: 'sparkle',
        name: 'Brilliance',
        keywords: ['magic', 'special', 'shine', 'brilliant', 'excellence'],
        bg: 'linear-gradient(135deg, #ffafbd 0%, #ffc3a0 100%)',
    },
    // Collaboration & Community
    {
        id: 'users',
        name: 'Teamwork',
        keywords: ['team', 'people', 'community', 'collaboration', 'together'],
        bg: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
    },
    {
        id: 'circle-users',
        name: 'Community',
        keywords: ['community', 'group', 'together', 'unity', 'collective'],
        bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
        id: 'hands-together',
        name: 'Unity',
        keywords: ['unity', 'together', 'collaboration', 'partnership', 'solidarity'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        id: 'bridge',
        name: 'Connection',
        keywords: ['bridge', 'connection', 'link', 'relationship', 'unite'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'chain',
        name: 'Solidarity',
        keywords: ['chain', 'link', 'connection', 'unity', 'strength'],
        bg: 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    },
    {
        id: 'puzzle',
        name: 'Synergy',
        keywords: ['solution', 'fit', 'teamwork', 'problem', 'collaboration'],
        bg: 'linear-gradient(135deg, #ff6a00 0%, #ee0979 100%)',
    },
    {
        id: 'network',
        name: 'Collaboration',
        keywords: ['connection', 'social', 'linked', 'community', 'network'],
        bg: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)',
    },
    {
        id: 'globe',
        name: 'Diversity',
        keywords: ['world', 'global', 'international', 'earth', 'diversity'],
        bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    },
    // Customer Focus
    {
        id: 'target',
        name: 'Focus',
        keywords: ['goal', 'focus', 'aim', 'objective', 'precision'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        id: 'user-star',
        name: 'Customer First',
        keywords: ['vip', 'customer', 'excellence', 'special', 'priority'],
        bg: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
    },
    {
        id: 'smile',
        name: 'Satisfaction',
        keywords: ['happy', 'smile', 'satisfaction', 'joy', 'customer'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'thumbs-up',
        name: 'Approval',
        keywords: ['good', 'approval', 'success', 'positive', 'satisfaction'],
        bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
        id: 'service-bell',
        name: 'Service',
        keywords: ['service', 'help', 'support', 'customer', 'hospitality'],
        bg: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    },
    {
        id: 'gift',
        name: 'Generosity',
        keywords: ['gift', 'giving', 'generous', 'present', 'kindness'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'heart-handshake',
        name: 'Commitment',
        keywords: ['commitment', 'promise', 'dedication', 'loyalty', 'care'],
        bg: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)',
    },
    {
        id: 'ear',
        name: 'Listening',
        keywords: ['listen', 'hear', 'attention', 'understanding', 'empathy'],
        bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    },
    // Learning & Wisdom
    {
        id: 'brain',
        name: 'Intelligence',
        keywords: ['intelligence', 'thinking', 'smart', 'mind', 'wisdom'],
        bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    },
    {
        id: 'book',
        name: 'Knowledge',
        keywords: ['knowledge', 'learning', 'education', 'wisdom', 'study'],
        bg: 'linear-gradient(135deg, #5f2c82 0%, #49a09d 100%)',
    },
    {
        id: 'graduation',
        name: 'Development',
        keywords: ['education', 'achievement', 'learning', 'degree', 'growth'],
        bg: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    },
    {
        id: 'eye',
        name: 'Vision',
        keywords: ['vision', 'see', 'observe', 'insight', 'perspective'],
        bg: 'linear-gradient(135deg, #6190e8 0%, #a7bfe8 100%)',
    },
    {
        id: 'microscope',
        name: 'Curiosity',
        keywords: ['research', 'science', 'discovery', 'analysis', 'curiosity'],
        bg: 'linear-gradient(135deg, #614385 0%, #516395 100%)',
    },
    {
        id: 'lamp',
        name: 'Wisdom',
        keywords: ['wisdom', 'knowledge', 'enlightenment', 'learning', 'insight'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'scroll',
        name: 'Heritage',
        keywords: ['history', 'tradition', 'heritage', 'legacy', 'wisdom'],
        bg: 'linear-gradient(135deg, #c79081 0%, #dfa579 100%)',
    },
    {
        id: 'owl',
        name: 'Wisdom',
        keywords: ['wise', 'knowledge', 'wisdom', 'insight', 'intelligence'],
        bg: 'linear-gradient(135deg, #5f2c82 0%, #49a09d 100%)',
    },
    // Sustainability & Nature
    {
        id: 'tree',
        name: 'Sustainability',
        keywords: ['nature', 'growth', 'environment', 'sustainability', 'life'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        id: 'leaf',
        name: 'Eco-Friendly',
        keywords: ['nature', 'eco', 'green', 'organic', 'environment'],
        bg: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    },
    {
        id: 'earth',
        name: 'Responsibility',
        keywords: ['planet', 'earth', 'environment', 'global', 'responsibility'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'sun',
        name: 'Positivity',
        keywords: ['energy', 'bright', 'optimism', 'warmth', 'positive'],
        bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    },
    {
        id: 'water',
        name: 'Adaptability',
        keywords: ['flow', 'adapt', 'flexibility', 'calm', 'resilience'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'flower',
        name: 'Growth',
        keywords: ['bloom', 'growth', 'beauty', 'nature', 'flourish'],
        bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    },
    {
        id: 'rainbow',
        name: 'Diversity',
        keywords: ['diversity', 'inclusion', 'color', 'variety', 'harmony'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'recycle',
        name: 'Renewal',
        keywords: ['recycle', 'renew', 'sustainability', 'cycle', 'environment'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    // Strength & Courage
    {
        id: 'fire',
        name: 'Determination',
        keywords: ['passion', 'energy', 'motivation', 'drive', 'determination'],
        bg: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    },
    {
        id: 'lion',
        name: 'Courage',
        keywords: ['brave', 'courage', 'strength', 'bold', 'fearless'],
        bg: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    },
    {
        id: 'fist',
        name: 'Strength',
        keywords: ['power', 'strength', 'determination', 'resolve', 'strong'],
        bg: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
    },
    {
        id: 'phoenix',
        name: 'Resilience',
        keywords: ['rebirth', 'resilience', 'rise', 'transform', 'overcome'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'wings',
        name: 'Freedom',
        keywords: ['freedom', 'fly', 'liberty', 'aspiration', 'limitless'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'sword',
        name: 'Determination',
        keywords: ['fight', 'determination', 'strength', 'courage', 'defend'],
        bg: 'linear-gradient(135deg, #636363 0%, #a2ab58 100%)',
    },
    {
        id: 'fortress',
        name: 'Stability',
        keywords: ['strong', 'fortress', 'stability', 'protection', 'endurance'],
        bg: 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    },
    {
        id: 'eagle',
        name: 'Vision',
        keywords: ['eagle', 'vision', 'freedom', 'perspective', 'soar'],
        bg: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    },
    // Creativity & Art
    {
        id: 'palette',
        name: 'Creativity',
        keywords: ['art', 'creative', 'color', 'design', 'expression'],
        bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    },
    {
        id: 'music',
        name: 'Harmony',
        keywords: ['music', 'harmony', 'rhythm', 'art', 'expression'],
        bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
        id: 'pen',
        name: 'Expression',
        keywords: ['write', 'create', 'express', 'art', 'communication'],
        bg: 'linear-gradient(135deg, #5f2c82 0%, #49a09d 100%)',
    },
    {
        id: 'camera',
        name: 'Perspective',
        keywords: ['capture', 'vision', 'perspective', 'moment', 'focus'],
        bg: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
    },
    {
        id: 'theater',
        name: 'Expression',
        keywords: ['drama', 'expression', 'art', 'emotion', 'creativity'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'brush',
        name: 'Artistry',
        keywords: ['paint', 'art', 'create', 'design', 'expression'],
        bg: 'linear-gradient(135deg, #ff6a00 0%, #ee0979 100%)',
    },
    {
        id: 'origami',
        name: 'Craftsmanship',
        keywords: ['craft', 'precision', 'art', 'skill', 'creation'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'diamond-alt',
        name: 'Refinement',
        keywords: ['polish', 'refine', 'perfect', 'craft', 'excellence'],
        bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    // Communication & Transparency
    {
        id: 'message',
        name: 'Communication',
        keywords: ['communication', 'chat', 'talk', 'discuss', 'dialogue'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'megaphone',
        name: 'Voice',
        keywords: ['announce', 'marketing', 'voice', 'promotion', 'speak'],
        bg: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
    },
    {
        id: 'open-book',
        name: 'Transparency',
        keywords: ['open', 'transparent', 'honest', 'clear', 'accessible'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'mirror',
        name: 'Reflection',
        keywords: ['reflect', 'introspection', 'honest', 'self-aware', 'truth'],
        bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    },
    {
        id: 'speech',
        name: 'Dialogue',
        keywords: ['speech', 'talk', 'communicate', 'express', 'discuss'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'window',
        name: 'Openness',
        keywords: ['open', 'transparent', 'clear', 'accessible', 'honest'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'prism',
        name: 'Clarity',
        keywords: ['clear', 'transparent', 'light', 'truth', 'perspective'],
        bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    {
        id: 'lantern',
        name: 'Enlightenment',
        keywords: ['light', 'guide', 'illuminate', 'truth', 'wisdom'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    // Time & Dedication
    {
        id: 'clock',
        name: 'Punctuality',
        keywords: ['time', 'punctual', 'schedule', 'efficiency', 'reliable'],
        bg: 'linear-gradient(135deg, #bc4e9c 0%, #f80759 100%)',
    },
    {
        id: 'infinity',
        name: 'Commitment',
        keywords: ['endless', 'unlimited', 'forever', 'continuous', 'dedication'],
        bg: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
    },
    {
        id: 'hourglass',
        name: 'Patience',
        keywords: ['time', 'patience', 'wait', 'process', 'endurance'],
        bg: 'linear-gradient(135deg, #c79081 0%, #dfa579 100%)',
    },
    {
        id: 'calendar',
        name: 'Consistency',
        keywords: ['schedule', 'planning', 'organize', 'date', 'consistent'],
        bg: 'linear-gradient(135deg, #4568dc 0%, #b06ab3 100%)',
    },
    {
        id: 'refresh',
        name: 'Adaptability',
        keywords: ['renew', 'cycle', 'update', 'restart', 'adapt'],
        bg: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    },
    {
        id: 'moon',
        name: 'Persistence',
        keywords: ['calm', 'night', 'peace', 'balance', 'steady'],
        bg: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',
    },
    {
        id: 'seasons',
        name: 'Evolution',
        keywords: ['change', 'seasons', 'growth', 'cycle', 'adapt'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        id: 'milestone',
        name: 'Progress',
        keywords: ['milestone', 'progress', 'achievement', 'journey', 'goal'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    // Safety & Care
    {
        id: 'lock',
        name: 'Security',
        keywords: ['security', 'privacy', 'protection', 'safe', 'trust'],
        bg: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
    },
    {
        id: 'key',
        name: 'Access',
        keywords: ['access', 'unlock', 'solution', 'important', 'opportunity'],
        bg: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    },
    {
        id: 'umbrella',
        name: 'Protection',
        keywords: ['protect', 'shelter', 'care', 'safety', 'support'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'home',
        name: 'Belonging',
        keywords: ['home', 'family', 'comfort', 'belonging', 'security'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'first-aid',
        name: 'Care',
        keywords: ['health', 'care', 'help', 'support', 'wellness'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'embrace',
        name: 'Warmth',
        keywords: ['hug', 'embrace', 'care', 'comfort', 'support'],
        bg: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
    },
    {
        id: 'nest',
        name: 'Nurturing',
        keywords: ['nurture', 'care', 'protect', 'grow', 'support'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'guardian',
        name: 'Protection',
        keywords: ['guard', 'protect', 'defend', 'watch', 'care'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    // Identity & Authenticity
    {
        id: 'fingerprint',
        name: 'Uniqueness',
        keywords: ['unique', 'identity', 'personal', 'authentic', 'individual'],
        bg: 'linear-gradient(135deg, #834d9b 0%, #d04ed6 100%)',
    },
    {
        id: 'flag',
        name: 'Identity',
        keywords: ['milestone', 'achievement', 'goal', 'victory', 'identity'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'signature',
        name: 'Authenticity',
        keywords: ['authentic', 'personal', 'unique', 'identity', 'genuine'],
        bg: 'linear-gradient(135deg, #5f2c82 0%, #49a09d 100%)',
    },
    {
        id: 'dna',
        name: 'Heritage',
        keywords: ['genetics', 'life', 'innovation', 'science', 'roots'],
        bg: 'linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)',
    },
    {
        id: 'heart-pulse',
        name: 'Vitality',
        keywords: ['life', 'health', 'energy', 'vitality', 'passion'],
        bg: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)',
    },
    {
        id: 'atom',
        name: 'Foundation',
        keywords: ['science', 'innovation', 'technology', 'core', 'fundamental'],
        bg: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    },
    {
        id: 'core',
        name: 'Essence',
        keywords: ['core', 'center', 'essence', 'fundamental', 'heart'],
        bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
        id: 'roots',
        name: 'Foundation',
        keywords: ['roots', 'foundation', 'origin', 'heritage', 'grounded'],
        bg: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    },
    // Strategic pillar icons (prototype catalogue)
    {
        id: 'chart-pie',
        name: 'Analytics',
        keywords: ['analytics', 'data', 'statistics', 'report'],
        bg: 'linear-gradient(135deg, #c33764 0%, #1d2671 100%)',
    },
    {
        id: 'chart-bar',
        name: 'Performance',
        keywords: ['performance', 'metrics', 'data', 'growth'],
        bg: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
    },
    {
        id: 'cloud',
        name: 'Cloud',
        keywords: ['technology', 'storage', 'digital', 'modern'],
        bg: 'linear-gradient(135deg, #757f9a 0%, #d7dde8 100%)',
    },
    {
        id: 'server',
        name: 'Infrastructure',
        keywords: ['technology', 'infrastructure', 'data', 'digital'],
        bg: 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    },
    {
        id: 'code',
        name: 'Development',
        keywords: ['development', 'programming', 'tech', 'software'],
        bg: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    },
    {
        id: 'terminal',
        name: 'Technical',
        keywords: ['coding', 'developer', 'tech', 'command'],
        bg: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    },
    {
        id: 'gear',
        name: 'Operations',
        keywords: ['settings', 'process', 'engineering', 'mechanical'],
        bg: 'linear-gradient(135deg, #636363 0%, #a2ab58 100%)',
    },
    {
        id: 'wrench',
        name: 'Tools',
        keywords: ['tool', 'fix', 'repair', 'build'],
        bg: 'linear-gradient(135deg, #544a7d 0%, #ffd452 100%)',
    },
    {
        id: 'hammer',
        name: 'Build',
        keywords: ['build', 'construct', 'work', 'craft'],
        bg: 'linear-gradient(135deg, #8e9eab 0%, #eef2f3 100%)',
    },
    {
        id: 'magnet',
        name: 'Attraction',
        keywords: ['attract', 'pull', 'magnetic', 'force'],
        bg: 'linear-gradient(135deg, #e53935 0%, #e35d5b 100%)',
    },
    {
        id: 'battery',
        name: 'Power',
        keywords: ['energy', 'power', 'charge', 'endurance'],
        bg: 'linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%)',
    },
    {
        id: 'wifi',
        name: 'Connectivity',
        keywords: ['connection', 'internet', 'wireless', 'online'],
        bg: 'linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)',
    },
    {
        id: 'airplane',
        name: 'Travel',
        keywords: ['travel', 'flight', 'journey', 'aviation'],
        bg: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
    },
    {
        id: 'building',
        name: 'Enterprise',
        keywords: ['business', 'corporate', 'office', 'building'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        id: 'briefcase',
        name: 'Business',
        keywords: ['work', 'job', 'career', 'professional'],
        bg: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    },
    {
        id: 'phone',
        name: 'Mobile',
        keywords: ['mobile', 'phone', 'communication', 'device'],
        bg: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)',
    },
    {
        id: 'mail',
        name: 'Email',
        keywords: ['email', 'message', 'contact', 'letter'],
        bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
        id: 'bell',
        name: 'Notification',
        keywords: ['alert', 'notification', 'reminder', 'attention'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'search-alt',
        name: 'Discovery',
        keywords: ['search', 'find', 'discover', 'explore'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'settings',
        name: 'Settings',
        keywords: ['config', 'settings', 'preferences', 'options'],
        bg: 'linear-gradient(135deg, #636363 0%, #a2ab58 100%)',
    },
    {
        id: 'share',
        name: 'Share',
        keywords: ['share', 'distribute', 'spread', 'social'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'download',
        name: 'Download',
        keywords: ['download', 'save', 'get', 'receive'],
        bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
        id: 'upload-alt',
        name: 'Upload',
        keywords: ['upload', 'send', 'share', 'transfer'],
        bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
        id: 'folder',
        name: 'Organization',
        keywords: ['folder', 'organize', 'files', 'storage'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'file',
        name: 'Document',
        keywords: ['file', 'document', 'paper', 'record'],
        bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    },
    {
        id: 'clipboard',
        name: 'Tasks',
        keywords: ['clipboard', 'tasks', 'list', 'checklist'],
        bg: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
    },
    {
        id: 'bookmark',
        name: 'Bookmark',
        keywords: ['bookmark', 'save', 'favorite', 'mark'],
        bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
        id: 'tag',
        name: 'Label',
        keywords: ['tag', 'label', 'category', 'classify'],
        bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    },
    {
        id: 'link',
        name: 'Connection',
        keywords: ['link', 'connect', 'url', 'chain'],
        bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    {
        id: 'map',
        name: 'Location',
        keywords: ['map', 'location', 'place', 'geography'],
        bg: 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)',
    },
    {
        id: 'pin',
        name: 'Pin',
        keywords: ['pin', 'location', 'marker', 'point'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'layers',
        name: 'Layers',
        keywords: ['layers', 'stack', 'depth', 'levels'],
        bg: 'linear-gradient(135deg, #5f2c82 0%, #49a09d 100%)',
    },
    {
        id: 'grid',
        name: 'Grid',
        keywords: ['grid', 'layout', 'structure', 'matrix'],
        bg: 'linear-gradient(135deg, #614385 0%, #516395 100%)',
    },
    {
        id: 'cpu',
        name: 'Processing',
        keywords: ['cpu', 'processor', 'computing', 'chip'],
        bg: 'linear-gradient(135deg, #0f0c29 0%, #302b63 100%)',
    },
    {
        id: 'database',
        name: 'Database',
        keywords: ['database', 'data', 'storage', 'records'],
        bg: 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    },
    {
        id: 'shield-check',
        name: 'Verified',
        keywords: ['verified', 'secure', 'trusted', 'safe'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
        id: 'alert',
        name: 'Alert',
        keywords: ['alert', 'warning', 'caution', 'attention'],
        bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    },
    {
        id: 'check-circle',
        name: 'Complete',
        keywords: ['complete', 'done', 'success', 'check'],
        bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
        id: 'x-circle',
        name: 'Cancel',
        keywords: ['cancel', 'close', 'remove', 'delete'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'info',
        name: 'Info',
        keywords: ['info', 'information', 'details', 'help'],
        bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
        id: 'help',
        name: 'Help',
        keywords: ['help', 'support', 'question', 'assist'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        id: 'zap',
        name: 'Speed',
        keywords: ['speed', 'fast', 'quick', 'lightning'],
        bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
        id: 'trending',
        name: 'Trending',
        keywords: ['trending', 'popular', 'rising', 'growth'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'pie-chart',
        name: 'Distribution',
        keywords: ['distribution', 'share', 'portion', 'segment'],
        bg: 'linear-gradient(135deg, #c33764 0%, #1d2671 100%)',
    },
    {
        id: 'bar-chart',
        name: 'Statistics',
        keywords: ['statistics', 'data', 'chart', 'analysis'],
        bg: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
    },
    {
        id: 'line-chart',
        name: 'Trends',
        keywords: ['trends', 'progress', 'timeline', 'history'],
        bg: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    },
    {
        id: 'activity',
        name: 'Activity',
        keywords: ['activity', 'pulse', 'health', 'monitor'],
        bg: 'linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)',
    },
    {
        id: 'heartbeat',
        name: 'Vitality',
        keywords: ['vitality', 'health', 'life', 'pulse'],
        bg: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    },
    {
        id: 'pill',
        name: 'Wellness',
        keywords: ['wellness', 'health', 'medicine', 'care'],
        bg: 'linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%)',
    },
    {
        id: 'stethoscope',
        name: 'Diagnosis',
        keywords: ['diagnosis', 'health', 'medical', 'check'],
        bg: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    },
    {
        id: 'sun-rise',
        name: 'New Beginning',
        keywords: ['sunrise', 'new', 'start', 'fresh'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'moon-stars',
        name: 'Dreams',
        keywords: ['dreams', 'night', 'vision', 'aspiration'],
        bg: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)',
    },
    {
        id: 'wind',
        name: 'Flexibility',
        keywords: ['flexibility', 'adapt', 'change', 'flow'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'snowflake',
        name: 'Unique',
        keywords: ['unique', 'special', 'individual', 'distinct'],
        bg: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    },
    {
        id: 'thermometer',
        name: 'Climate',
        keywords: ['climate', 'temperature', 'environment', 'measure'],
        bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    },
    {
        id: 'droplet',
        name: 'Resource',
        keywords: ['resource', 'water', 'essential', 'supply'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'flame',
        name: 'Intensity',
        keywords: ['intensity', 'passion', 'energy', 'drive'],
        bg: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)',
    },
    {
        id: 'seedling',
        name: 'Growth',
        keywords: ['growth', 'new', 'potential', 'develop'],
        bg: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    },
    {
        id: 'fruit',
        name: 'Results',
        keywords: ['results', 'harvest', 'outcome', 'reward'],
        bg: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
    },
    {
        id: 'wheat',
        name: 'Abundance',
        keywords: ['abundance', 'prosperity', 'plenty', 'harvest'],
        bg: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    },
    {
        id: 'eco',
        name: 'Eco-Friendly',
        keywords: ['eco', 'green', 'environment', 'nature'],
        bg: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
    },
    {
        id: 'planet',
        name: 'Global',
        keywords: ['global', 'world', 'earth', 'planet'],
        bg: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    },
    {
        id: 'satellite',
        name: 'Reach',
        keywords: ['reach', 'expand', 'coverage', 'broadcast'],
        bg: 'linear-gradient(135deg, #0f2027 0%, #2c5364 100%)',
    },
    {
        id: 'antenna',
        name: 'Broadcast',
        keywords: ['broadcast', 'signal', 'transmit', 'reach'],
        bg: 'linear-gradient(135deg, #757f9a 0%, #d7dde8 100%)',
    },
    {
        id: 'radio',
        name: 'Communication',
        keywords: ['radio', 'broadcast', 'media', 'voice'],
        bg: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
    },
    {
        id: 'tv',
        name: 'Media',
        keywords: ['media', 'screen', 'display', 'broadcast'],
        bg: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
    },
    {
        id: 'monitor',
        name: 'Display',
        keywords: ['display', 'screen', 'monitor', 'view'],
        bg: 'linear-gradient(135deg, #373b44 0%, #4286f4 100%)',
    },
    {
        id: 'laptop',
        name: 'Remote',
        keywords: ['remote', 'work', 'digital', 'portable'],
        bg: 'linear-gradient(135deg, #636363 0%, #a2ab58 100%)',
    },
    {
        id: 'tablet',
        name: 'Mobile First',
        keywords: ['mobile', 'tablet', 'device', 'portable'],
        bg: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)',
    },
    {
        id: 'watch',
        name: 'Time',
        keywords: ['time', 'watch', 'punctual', 'schedule'],
        bg: 'linear-gradient(135deg, #bc4e9c 0%, #f80759 100%)',
    },
    {
        id: 'headphones',
        name: 'Listen',
        keywords: ['listen', 'audio', 'music', 'sound'],
        bg: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
    },
    {
        id: 'mic',
        name: 'Voice',
        keywords: ['voice', 'speak', 'record', 'audio'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'speaker',
        name: 'Amplify',
        keywords: ['amplify', 'sound', 'loud', 'broadcast'],
        bg: 'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)',
    },
    {
        id: 'volume',
        name: 'Sound',
        keywords: ['sound', 'volume', 'audio', 'hear'],
        bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
        id: 'play',
        name: 'Action',
        keywords: ['action', 'play', 'start', 'begin'],
        bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
        id: 'pause',
        name: 'Pause',
        keywords: ['pause', 'wait', 'stop', 'break'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'stop',
        name: 'Stop',
        keywords: ['stop', 'end', 'halt', 'finish'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'forward',
        name: 'Advance',
        keywords: ['advance', 'forward', 'next', 'progress'],
        bg: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
    },
    {
        id: 'rewind',
        name: 'Review',
        keywords: ['review', 'back', 'rewind', 'history'],
        bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
    },
    {
        id: 'repeat',
        name: 'Repeat',
        keywords: ['repeat', 'cycle', 'loop', 'again'],
        bg: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
    },
    {
        id: 'shuffle',
        name: 'Random',
        keywords: ['random', 'mix', 'variety', 'shuffle'],
        bg: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    },
    {
        id: 'maximize',
        name: 'Expand',
        keywords: ['expand', 'maximize', 'grow', 'full'],
        bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
        id: 'minimize',
        name: 'Focus',
        keywords: ['focus', 'minimize', 'concentrate', 'reduce'],
        bg: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    {
        id: 'crop',
        name: 'Precision',
        keywords: ['precision', 'crop', 'exact', 'focus'],
        bg: 'linear-gradient(135deg, #614385 0%, #516395 100%)',
    },
    {
        id: 'rotate',
        name: 'Transform',
        keywords: ['transform', 'rotate', 'change', 'turn'],
        bg: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    },
    {
        id: 'flip',
        name: 'Perspective',
        keywords: ['perspective', 'flip', 'view', 'angle'],
        bg: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
    },
    {
        id: 'filter',
        name: 'Filter',
        keywords: ['filter', 'sort', 'refine', 'select'],
        bg: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    },
    {
        id: 'sliders',
        name: 'Adjust',
        keywords: ['adjust', 'tune', 'control', 'customize'],
        bg: 'linear-gradient(135deg, #636363 0%, #a2ab58 100%)',
    },
    {
        id: 'toggle',
        name: 'Switch',
        keywords: ['switch', 'toggle', 'change', 'option'],
        bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
        id: 'power',
        name: 'Power',
        keywords: ['power', 'energy', 'start', 'activate'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'bolt',
        name: 'Electric',
        keywords: ['electric', 'power', 'energy', 'charge'],
        bg: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
    },
    {
        id: 'plug',
        name: 'Connect',
        keywords: ['connect', 'plug', 'power', 'link'],
        bg: 'linear-gradient(135deg, #56ccf2 0%, #2f80ed 100%)',
    },
    {
        id: 'outlet',
        name: 'Source',
        keywords: ['source', 'power', 'supply', 'origin'],
        bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
        id: 'solar',
        name: 'Solar',
        keywords: ['solar', 'sun', 'renewable', 'energy'],
        bg: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
    },
    {
        id: 'windmill',
        name: 'Wind Power',
        keywords: ['wind', 'renewable', 'energy', 'green'],
        bg: 'linear-gradient(135deg, #00c6fb 0%, #005bea 100%)',
    },
    {
        id: 'factory',
        name: 'Production',
        keywords: ['production', 'factory', 'manufacturing', 'industry'],
        bg: 'linear-gradient(135deg, #434343 0%, #000000 100%)',
    },
    {
        id: 'warehouse',
        name: 'Storage',
        keywords: ['storage', 'warehouse', 'inventory', 'supply'],
        bg: 'linear-gradient(135deg, #8e9eab 0%, #eef2f3 100%)',
    },
    {
        id: 'truck',
        name: 'Delivery',
        keywords: ['delivery', 'transport', 'logistics', 'shipping'],
        bg: 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)',
    },
    {
        id: 'ship',
        name: 'Maritime',
        keywords: ['maritime', 'ship', 'ocean', 'transport'],
        bg: 'linear-gradient(135deg, #1a2980 0%, #26d0ce 100%)',
    },
    {
        id: 'train',
        name: 'Rail',
        keywords: ['rail', 'train', 'transport', 'journey'],
        bg: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    },
    {
        id: 'car',
        name: 'Automotive',
        keywords: ['automotive', 'car', 'vehicle', 'transport'],
        bg: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
    },
    {
        id: 'bicycle',
        name: 'Sustainable Transport',
        keywords: ['bicycle', 'eco', 'health', 'transport'],
        bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
];
